import cn from 'classnames';
import { useIntl } from 'react-intl';

import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { StudyMetaInfo } from '@/shared/config/types';
import { useMedia } from '@/shared/hooks';
import { getRawMeshViewerLink, openInNewBrowserTab } from '@/shared/lib';
import { Button, Description } from '@/shared/ui';

import { getStudyArchiveURL } from '@/entities/assets';

import { i18n } from '../../config';

import { ScanUidCopy } from './ScanUidCopy/ScanUidCopy';
import styles from './StudyInfo.module.scss';

type StudyInfoProps = StudyMetaInfo & {
  className?: string;
  showViewIntraoralScanButton?: boolean;
};

export const StudyInfo = (props: StudyInfoProps) => {
  const {
    className,
    showViewIntraoralScanButton = false,
    ID,
    studyType,
    uploadDate,
    studyTime,
    scanUID,
    manufacturer,
    model,
    voxel,
    fov,
    exposure,
  } = props;

  const { formatMessage, formatDate } = useIntl();
  const { isMobile } = useMedia();

  const title = formatMessage(i18n.studyTypes[studyType]);
  const isCBCT = studyType === StudyType.StudyType_CBCT;
  const dateLabel = isCBCT
    ? formatMessage(i18n.studyScanInfoMessages.date)
    : formatMessage({
        id: 'studyInfo.uploadDate',
        defaultMessage: 'Upload date',
      });

  const downloadStudy = () => {
    window.open(getStudyArchiveURL(ID), '_blank');
  };

  const openRawMeshViewer = () => {
    openInNewBrowserTab(getRawMeshViewerLink(ID));
  };

  const labelClassName = cn({
    [styles.descriptionLabel]: !isCBCT,
    p2: !isMobile,
    p1m: isMobile,
  });

  const descriptionContentClassName = cn(styles.descriptionContent, {
    p2: !isMobile,
    p1m: isMobile,
  });

  return (
    <section
      className={cn(styles.container, className)}
      data-component="study-info"
    >
      <header className={styles.header}>
        <h4 className={cn({ h4: !isMobile, h2m: isMobile })}>{title}</h4>
      </header>
      <main className={styles.main}>
        <div className={styles.col}>
          {isCBCT && (
            <Description
              label={formatMessage({
                id: 'studyInfo.scanUID',
                defaultMessage: 'Scan UID',
              })}
              className={styles.descriptionContainer}
              labelClassName={labelClassName}
              contentClassName={descriptionContentClassName}
              hideColon
              isVertical
            >
              <ScanUidCopy scanUid={scanUID ?? ''} />
            </Description>
          )}
          <Description
            label={dateLabel}
            className={cn(
              styles.descriptionContainer,
              !isCBCT && styles.alignCenter,
            )}
            labelClassName={labelClassName}
            contentClassName={descriptionContentClassName}
            hideColon
            isVertical={isCBCT}
          >
            {formatDate(studyTime ?? uploadDate, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </Description>
          {isCBCT && (
            <Description
              label={formatMessage(i18n.studyScanInfoMessages.manufacturer)}
              className={styles.descriptionContainer}
              labelClassName={labelClassName}
              contentClassName={descriptionContentClassName}
              hideColon
              isVertical
            >
              {manufacturer || '-'}
            </Description>
          )}
          {isCBCT && (
            <Description
              label={formatMessage(i18n.studyScanInfoMessages.model)}
              className={styles.descriptionContainer}
              labelClassName={labelClassName}
              contentClassName={descriptionContentClassName}
              hideColon
              isVertical
            >
              {model || '-'}
            </Description>
          )}
        </div>
        {isCBCT && (
          <div className={styles.col}>
            <Description
              label={formatMessage(i18n.studyScanInfoMessages.voxel)}
              className={styles.descriptionContainer}
              labelClassName={labelClassName}
              contentClassName={descriptionContentClassName}
              hideColon
              isVertical
            >
              {voxel || '-'}
            </Description>
            <Description
              label={formatMessage(i18n.studyScanInfoMessages.fov)}
              className={styles.descriptionContainer}
              labelClassName={labelClassName}
              contentClassName={descriptionContentClassName}
              hideColon
              isVertical
            >
              {fov || '-'}
            </Description>
            <Description
              label={formatMessage(i18n.studyScanInfoMessages.exposure)}
              className={styles.descriptionContainer}
              labelClassName={labelClassName}
              contentClassName={descriptionContentClassName}
              hideColon
              isVertical
            >
              {exposure || '-'}
            </Description>
          </div>
        )}
      </main>
      <footer className={styles.footer}>
        {showViewIntraoralScanButton && (
          <Button
            className={styles.footerButton}
            size={isMobile ? undefined : 'tiny'}
            variant="secondary"
            onClick={openRawMeshViewer}
          >
            {isMobile
              ? formatMessage({
                  id: 'global.view',
                  defaultMessage: 'View',
                })
              : formatMessage({
                  id: 'reportInfoModal.viewIntraoralScan',
                  defaultMessage: 'View Intraoral Scan',
                })}
          </Button>
        )}
        {isMobile ? (
          <Button
            className={styles.footerButton}
            variant="secondary"
            onClick={downloadStudy}
          >
            {formatMessage({
              id: 'global.download',
              defaultMessage: 'Download',
            })}
          </Button>
        ) : (
          <Button size="tiny" variant="secondary" onClick={downloadStudy}>
            {formatMessage({
              id: 'global.download',
              defaultMessage: 'Download',
            })}{' '}
            {title}
          </Button>
        )}
      </footer>
    </section>
  );
};
